import React, { useRef, useState, useCallback, useEffect } from 'react';
import { CATEGORIES } from '@/data/categories';

// Short display labels for wheel UI only — MUST match CATEGORIES order exactly
const WHEEL_LABELS = [
  'Listen',    // 0  Listen
  'Express',   // 1  Express
  'Self',      // 2  Self-Command
  'Focus',     // 3  Focus
  'Decide',    // 4  Decide
  'See',       // 5  See
  'Team',      // 6  Collaborate
  'Navigate',  // 7  Navigate
  'Own It',    // 8  Own It
  'Adapt',     // 9  Adapt
  'Create',    // 10 Create
  'Steady'     // 11 Steady
];

// 12 distinct colors — same index order as CATEGORIES
const CATEGORY_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
  '#BB8FCE', '#85C1E9', '#F8B500', '#FF8C94'
];

const LIGHT_COLORS = new Set(['#FFEAA7', '#F7DC6F', '#96CEB4', '#98D8C8']);

// ---------- geometry helpers (pure, no side effects) ----------

/** Read the actual rotation angle the browser is currently painting.
 *  Returns 0-360 (clockwise degrees). */
function readRenderedAngle(svgEl) {
  if (!svgEl) return 0;
  const matrix = window.getComputedStyle(svgEl).transform;
  if (!matrix || matrix === 'none') return 0;
  const m = matrix.match(/matrix\((.+)\)/);
  if (!m) return 0;
  const [a, b] = m[1].split(',').map(Number);
  const deg = Math.atan2(b, a) * (180 / Math.PI);
  return ((deg % 360) + 360) % 360;
}

/** Given the rendered clockwise rotation of the SVG (0-360),
 *  return the wedge index whose CENTER is closest to the top pointer.
 *
 *  Coordinate system (canvas / SVG): 0° = right, CW positive, 270° = top.
 *  Wedges are drawn starting at −90° (= 270°), so wedge i centre in
 *  canvas coords = (270 + i·30 + 15) % 360.
 */
function closestWedge(renderedAngle, total = 12) {
  const wedgeAngle = 360 / total;
  const normRot = ((renderedAngle % 360) + 360) % 360;
  const pointerAngle = 270;                                      // top
  const effectiveAngle = (pointerAngle - normRot + 360) % 360;   // pointer in wheel-local

  let closestIndex = 0;
  let smallestDiff = Infinity;

  for (let i = 0; i < total; i++) {
    const centerAngle = (270 + i * wedgeAngle + wedgeAngle / 2) % 360;
    let diff = Math.abs(centerAngle - effectiveAngle);
    if (diff > 180) diff = 360 - diff;
    if (diff < smallestDiff) {
      smallestDiff = diff;
      closestIndex = i;
    }
  }

  return {
    index: closestIndex,
    effectiveAngle,
    pointerAngle,
    normalizedRotation: normRot,
    smallestDiff,
  };
}

// ---------- component ----------

export const Wheel = ({ onSpinComplete, disabled, hasSpunToday, isDemo = false }) => {
  const wheelRef = useRef(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);



  // Keep a stable ref to onSpinComplete so the transitionend
  // handler always calls the latest version without re-subscribing.
  const onSpinCompleteRef = useRef(onSpinComplete);
  useEffect(() => { onSpinCompleteRef.current = onSpinComplete; }, [onSpinComplete]);

  const totalSegments = CATEGORIES.length;
  const segmentAngle = 360 / totalSegments;

  // SVG geometry
  const viewBoxSize = 400;
  const cx = viewBoxSize / 2;
  const cy = viewBoxSize / 2;
  const outerR = viewBoxSize / 2 - 8;
  const innerR = 24;
  const labelR = (outerR + innerR) / 2 + 18;

  // ---- SPIN (only picks a random target — NO category logic here) ----
  const handleSpin = useCallback(() => {
    if (isSpinning || (disabled && !isDemo)) return;

    const extra = (5 + Math.random() * 3) * 360;   // 5-8 full rotations
    const offset = Math.random() * 360;              // random landing angle
    const target = rotation + extra + offset;

    setIsSpinning(true);
    setRotation(target);



   }, [isSpinning, disabled, isDemo, rotation]);

  // ---- RESOLVE SPIN from the ACTUAL rendered rotation ----
  useEffect(() => {
    const el = wheelRef.current;
    if (!el || !isSpinning) return;

    let resolved = false;

    const resolve = () => {
      if (resolved) return;
      resolved = true;

      // SINGLE SOURCE OF TRUTH: the angle the browser actually painted
      const angle = readRenderedAngle(el);
      const result = closestWedge(angle, totalSegments);
      const cat = CATEGORIES[result.index];

      setIsSpinning(false);
      onSpinCompleteRef.current(cat);
    };

    const onTransitionEnd = (e) => {
      if (e.propertyName === 'transform') resolve();
    };
    el.addEventListener('transitionend', onTransitionEnd);

    // Safety fallback in case transitionend doesn't fire
    const fallback = setTimeout(resolve, 5500);

    return () => {
      el.removeEventListener('transitionend', onTransitionEnd);
      clearTimeout(fallback);
    };
  }, [isSpinning, rotation, totalSegments]);

  // ---- SVG segment generation ----
  const segments = [];
  for (let i = 0; i < totalSegments; i++) {
    const startAngle = i * segmentAngle - 90;
    const endAngle   = startAngle + segmentAngle;
    const startRad   = (startAngle * Math.PI) / 180;
    const endRad     = (endAngle   * Math.PI) / 180;

    const x1 = cx + outerR * Math.cos(startRad);
    const y1 = cy + outerR * Math.sin(startRad);
    const x2 = cx + outerR * Math.cos(endRad);
    const y2 = cy + outerR * Math.sin(endRad);
    const ix1 = cx + innerR * Math.cos(startRad);
    const iy1 = cy + innerR * Math.sin(startRad);
    const ix2 = cx + innerR * Math.cos(endRad);
    const iy2 = cy + innerR * Math.sin(endRad);

    const arc = segmentAngle > 180 ? 1 : 0;
    const d = `M ${ix1} ${iy1} L ${x1} ${y1} A ${outerR} ${outerR} 0 ${arc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerR} ${innerR} 0 ${arc} 0 ${ix1} ${iy1} Z`;

    const midRad = ((startAngle + segmentAngle / 2) * Math.PI) / 180;
    const lx = cx + labelR * Math.cos(midRad);
    const ly = cy + labelR * Math.sin(midRad);

    const color = CATEGORY_COLORS[i];
    const light = LIGHT_COLORS.has(color);
    const fg    = light ? '#1F2937' : '#FFFFFF';

    const counterRotateStyle = {
      transformBox: 'fill-box',
      transformOrigin: 'center',
      transform: `rotate(${-rotation}deg)`,
      transition: isSpinning ? 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
    };

    segments.push(
      <g key={i}>
        <path d={d} fill={color} stroke="#F7F7F5" strokeWidth="2" />
        <text
          x={lx} y={ly} fill={fg}
          fontSize="11" fontWeight="600"
          textAnchor="middle" dominantBaseline="middle"
          className="pointer-events-none select-none"
          style={{
            textShadow: light ? 'none' : '0 1px 2px rgba(0,0,0,0.25)',
            fontFamily: 'Inter, sans-serif',
            letterSpacing: '0.02em',
            ...counterRotateStyle,
          }}
        >
          {WHEEL_LABELS[i]}
        </text>
      </g>
    );
  }

  const isDisabled = disabled && !isDemo;

  return (
    <div className="relative flex flex-col items-center w-full">
      {/* Pointer at 12 o'clock */}
      <div
        className={`absolute top-0 left-1/2 z-20 -translate-x-1/2 ${!isDisabled && !isSpinning ? 'pointer-ready' : ''}`}
        style={{ marginTop: '-10px' }}
      >
        <svg width="32" height="40" viewBox="0 0 32 40">
          <defs>
            <filter id="pointerShadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="hsl(175, 77%, 26%)" floodOpacity="0.4"/>
            </filter>
          </defs>
          <polygon points="16,40 4,10 16,16 28,10" fill="hsl(175, 77%, 26%)" filter="url(#pointerShadow)" />
        </svg>
      </div>

      {/* Wheel */}
      <div className="relative shadow-wheel rounded-full w-[60vw] max-w-[380px] aspect-square">
        <svg
          ref={wheelRef}
          viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
          className="w-full h-full transition-transform"
          style={{
            transform: `rotate(${rotation}deg)`,
            transitionDuration: isSpinning ? '5s' : '0s',
            transitionTimingFunction: 'cubic-bezier(0.17, 0.67, 0.12, 0.99)',
          }}
        >
          {segments}
          {/* Center hub */}
          <circle cx={cx} cy={cy} r={innerR + 2} fill="none" stroke="hsl(175, 77%, 26%)" strokeWidth="2" />
          <circle cx={cx} cy={cy} r={innerR} fill="hsl(35, 25%, 94%)" />
          <circle cx={cx} cy={cy} r={innerR - 5} fill="hsl(35, 20%, 90%)" />
          <circle cx={cx} cy={cy} r={5} fill="hsl(175, 77%, 26%)" />
        </svg>
      </div>

      {/* Spin button */}
      <button
        onClick={handleSpin}
        disabled={isDisabled || isSpinning}
        className={`
          mt-6 px-8 py-3 rounded-lg font-semibold text-base transition-all duration-200
          ${isDisabled ? 'bg-secondary text-helper cursor-not-allowed' : 'bg-primary text-primary-foreground hover:bg-primary-hover spin-btn-ready'}
          ${isSpinning ? 'animate-pulse' : ''}
        `}
      >
        {isSpinning ? 'Spinning…' : hasSpunToday && !isDemo ? 'Today spin complete' : 'Spin'}
      </button>

      {hasSpunToday && !isSpinning && !isDemo && (
        <p className="mt-2 text-sm text-helper">Come back tomorrow for a new skill</p>
      )}



    </div>
  );
};

export default Wheel;
